import { each, meanBy, orderBy } from 'lodash';
import { registerEventListener, removeEventListener } from './Utility';

const BOOLEAN_PARAMS = new Set([
  'anticipationDisabled',
  'autoPlayEnabled',
  'bonusBuyDisabled',
  'currencyDisplayEnabled',
  'devtoolsEnabled',
  'extendAutoPlayOptions',
  'isDemo',
  'isDummyWinGrades',
  'landscapeMode',
  'lightningModeDisabled',
  'lightningSpinDisabled',
  'lobbyEnabled',
  'mute',
  'showMaxBetWinLimits',
  'showPaytableOnLoad',
  'showRtpRangeLabel',
  'stopSpinDisabled',
]);

const NUMBER_PARAMS = new Set([
  'minRoundDurationMs',
]);

// Omit unchanged params from the URL. lobbyEnabled stays true for local serving.
const DEFAULT_QUERY_PARAMS = {
  autoPlayEnabled: true,
  baseUrl: 'https://casino-game.staging.de-3.nsoft.cloud',
  boostTenantId: '057d758c-57bb-4c40-9696-00957c5d6cb2',
  devtoolsEnabled: false,
  isDemo: true, // baseline when no playerToken; see getDefaultParamValue
  lobbyEnabled: true,
  mute: true,
  storageUrl: 'https://storage.googleapis.com/nsoftcasino-dev',
};

const HOST_ALLOWLIST = ['staging.games-webgl.7platform.net'];

// Match public.js: isDemo is !playerToken when the flag is omitted from the URL.
function getDefaultParamValue(key) {
  if (key === 'isDemo') {
    const hasPlayerToken = Boolean(new URLSearchParams(window.location.search).get('playerToken'));
    return !hasPlayerToken;
  }

  return DEFAULT_QUERY_PARAMS[key];
}

function createElement(tag, styles, textContent) {
  const element = document.createElement(tag);

  Object.assign(element.style, styles || {});

  if (textContent !== undefined) {
    element.textContent = textContent;
  }

  return element;
}

const BUTTON_STYLES = {
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  boxSizing: 'border-box',
  margin: '0',
  padding: '6px 8px',
  border: '1px solid #666',
  borderRadius: '4px',
  backgroundColor: '#222',
  color: '#fff',
  fontFamily: 'Arial, sans-serif',
  fontSize: '12px',
  fontWeight: 'bold',
  lineHeight: '1.2',
  cursor: 'pointer',
  touchAction: 'manipulation',
};

function createButton(textContent, styles) {
  const button = createElement('button', {
    ...BUTTON_STYLES,
    ...(styles || {}),
  }, textContent);
  button.type = 'button';
  return button;
}

function getParamType(key) {
  if (BOOLEAN_PARAMS.has(key)) return 'boolean';
  if (NUMBER_PARAMS.has(key)) return 'number';
  return 'string';
}

function getCurrentQueryParams() {
  if (typeof window.getQueryParams !== 'function') {
    return null;
  }

  const currentTenantGameId = new URLSearchParams(window.location.search).get('tenantGameId') || undefined;
  return window.getQueryParams(currentTenantGameId);
}

function navigateWithReload(nextParams) {
  const query = nextParams.toString();
  const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;

  if (nextUrl === currentUrl) {
    window.location.reload();
    return;
  }

  window.location.replace(nextUrl);
}

function applyQueryParams(controls) {
  const nextParams = new URLSearchParams(window.location.search);

  each(controls, (control, key) => {
    const type = getParamType(key);

    if (type === 'boolean') {
      const defaultValue = getDefaultParamValue(key);
      const { checked } = control;

      if (defaultValue !== undefined) {
        if (checked === defaultValue) {
          nextParams.delete(key);
        } else {
          nextParams.set(key, checked ? 'true' : 'false');
        }
        return;
      }

      if (checked) {
        nextParams.set(key, 'true');
      } else {
        nextParams.delete(key);
      }
      return;
    }

    const value = String(control.value ?? '').trim();
    const defaultValueString = String(getDefaultParamValue(key) ?? '');

    if (!value || value === defaultValueString) {
      nextParams.delete(key);
      return;
    }

    nextParams.set(key, value);
  });

  navigateWithReload(nextParams);
}

function clearQueryParams(controls) {
  const nextParams = new URLSearchParams(window.location.search);

  Object.keys(controls).forEach((key) => {
    nextParams.delete(key);
  });

  navigateWithReload(nextParams);
}

function createQueryParamsDrawer() {
  const queryParams = getCurrentQueryParams();
  if (!queryParams) return null;

  const controls = {};
  const drawer = createElement('div', {
    display: 'none',
    flex: '1 1 auto',
    minHeight: '0',
    marginTop: '8px',
    padding: '8px 0 0',
    borderTop: '1px solid #444',
    textAlign: 'left',
    fontWeight: 'normal',
    overflowY: 'auto',
    overscrollBehavior: 'none',
  });

  const rawSearchParams = new URLSearchParams(window.location.search);

  each(queryParams, (value, key) => {
    const type = getParamType(key);
    const row = createElement('div', {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '6px',
    });
    const label = createElement('label', {
      flex: '0 0 160px',
      fontSize: '12px',
    }, key);

    let control;

    if (type === 'boolean') {
      control = document.createElement('input');
      control.type = 'checkbox';
      const rawValue = rawSearchParams.get(key);
      if (rawValue === 'true') {
        control.checked = true;
      } else if (rawValue === 'false') {
        control.checked = false;
      } else {
        const defaultValue = getDefaultParamValue(key);
        control.checked = defaultValue !== undefined ? defaultValue : value === true;
      }
    } else {
      control = document.createElement('input');
      control.type = type === 'number' ? 'number' : 'text';
      control.value = value ?? '';
      Object.assign(control.style, {
        flex: '1',
        minWidth: '0',
        fontSize: '12px',
      });
    }

    controls[key] = control;
    row.appendChild(label);
    row.appendChild(control);
    drawer.appendChild(row);
  });

  const actions = createElement('div', {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '12px',
  });

  const applyButton = createButton('Apply', {
    flex: '1 1 50%',
  });
  applyButton.addEventListener('click', () => applyQueryParams(controls));

  const clearButton = createButton('Reset', {
    flex: '1 1 50%',
  });
  clearButton.addEventListener('click', () => clearQueryParams(controls));

  actions.appendChild(applyButton);
  actions.appendChild(clearButton);
  drawer.appendChild(actions);

  return drawer;
}

function createMetricsSection(pixiAppInstance, {
  startEventName = 'GameRevealed',
  metricsWindowMs = 60_000,
  panelUpdateMs = 1000,
} = {}) {
  const eventSource = 'DevtoolsMetricsPanel';
  const { ticker } = pixiAppInstance;

  const metricsElement = createElement('div', {
    color: 'white',
  });

  const getDevicePixelRatio = () => window.devicePixelRatio || 1;
  const getDeviceResolution = () => {
    const dpr = getDevicePixelRatio();
    return `${Math.round(window.screen.width * dpr)}x${Math.round(window.screen.height * dpr)}`;
  };
  const getFrameResolution = () => {
    const { renderer } = pixiAppInstance;
    return `${Math.round(renderer.width)}x${Math.round(renderer.height)}`;
  };

  const fpsLineElement = createElement('div', {}, '...');
  const frameTimeLineElement = createElement('div', {}, '...');
  const deviceInfoLineElement = createElement('div', {}, `DPR: ${getDevicePixelRatio()} | DRES: ${getDeviceResolution()} | FRES: ${getFrameResolution()}`);

  metricsElement.appendChild(fpsLineElement);
  metricsElement.appendChild(frameTimeLineElement);
  metricsElement.appendChild(deviceInfoLineElement);

  const frameSamples = [];
  let ticks = 0;
  let lastUpdateTime = 0;
  let lastFrameTime = 0;
  let isTracking = false;
  let isDisposed = false;
  let isMetricsPaused = document.hidden;

  const resetMetrics = (now = performance.now()) => {
    frameSamples.length = 0;
    ticks = 0;
    lastUpdateTime = now;
    lastFrameTime = now;
  };

  const showPausedMetrics = () => {
    metricsElement.style.color = 'gray';
    fpsLineElement.textContent = 'FPS: paused | AVG: paused | 1%: paused';
    frameTimeLineElement.textContent = 'FT: paused | AVG: paused | 1%: paused';
    deviceInfoLineElement.textContent = `DPR: ${getDevicePixelRatio()} | DRES: ${getDeviceResolution()} | FRES: ${getFrameResolution()}`;
  };

  const handleVisibilityChange = () => {
    isMetricsPaused = document.hidden;
    resetMetrics();

    if (isMetricsPaused) {
      showPausedMetrics();
    }
  };

  const updatePanel = (now) => {
    const elapsedMs = now - lastUpdateTime;
    const fps = Math.round((ticks * 1000) / elapsedMs);
    const currentFrameMs = ticks > 0 ? elapsedMs / ticks : 0;
    const averageFrameMs = frameSamples.length ? meanBy(frameSamples, 'frameMs') : 0;
    const averageFps = averageFrameMs > 0 ? Math.round(1000 / averageFrameMs) : 0;
    const onePercentFrameCount = Math.max(1, Math.ceil(frameSamples.length * 0.01));
    const slowestFrames = orderBy(frameSamples, 'frameMs', 'desc').slice(0, onePercentFrameCount);
    const onePercentFrameMs = slowestFrames.length ? meanBy(slowestFrames, 'frameMs') : 0;
    const onePercentLowFps = onePercentFrameMs > 0 ? Math.round(1000 / onePercentFrameMs) : 0;

    if (fps < 30) {
      metricsElement.style.color = 'red';
    } else if (fps < 50) {
      metricsElement.style.color = 'orange';
    } else {
      metricsElement.style.color = 'green';
    }

    fpsLineElement.textContent = `FPS: ${fps} | AVG: ${averageFps} | 1%: ${onePercentLowFps}`;
    frameTimeLineElement.textContent = `FT: ${currentFrameMs.toFixed(1)}ms | AVG: ${averageFrameMs.toFixed(1)}ms | 1%: ${onePercentFrameMs.toFixed(1)}ms`;
    deviceInfoLineElement.textContent = `DPR: ${getDevicePixelRatio()} | DRES: ${getDeviceResolution()} | FRES: ${getFrameResolution()}`;
    ticks = 0;
    lastUpdateTime = now;
  };

  const onTick = () => {
    if (isMetricsPaused) return;

    const now = performance.now();
    const frameMs = now - lastFrameTime;
    lastFrameTime = now;

    // Ignore resume/debugger/OS-sleep gaps. Real jank stays in the 1% sample.
    if (frameMs > 1000) {
      resetMetrics(now);
      return;
    }

    if (frameMs > 0) {
      frameSamples.push({ time: now, frameMs });
    }

    while (frameSamples.length && now - frameSamples[0].time > metricsWindowMs) {
      frameSamples.shift();
    }

    ticks += 1;

    if (now - lastUpdateTime >= panelUpdateMs) {
      updatePanel(now);
    }
  };

  const startTracking = () => {
    if (isDisposed || isTracking) return;

    isTracking = true;
    isMetricsPaused = document.hidden;
    resetMetrics();

    ticker.add(onTick);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    if (isMetricsPaused) {
      showPausedMetrics();
    }
  };

  registerEventListener(startEventName, startTracking, eventSource, true);

  return {
    element: metricsElement,
    destroy: () => {
      isDisposed = true;
      removeEventListener(startEventName, eventSource);

      if (isTracking) {
        ticker.remove(onTick);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    },
  };
}

function canEnableDevtoolsViaQueryParam() {
  if (process.env.NODE_ENV === 'development') {
    return true;
  }

  return HOST_ALLOWLIST.includes(window.location.hostname);
}

function isDevtoolsEnabledFromUrl() {
  if (!canEnableDevtoolsViaQueryParam()) {
    return false;
  }

  return new URLSearchParams(window.location.search).get('devtoolsEnabled') === 'true';
}

// Overlay with live ticker metrics and a collapsible query-param drawer (opt-in via devtoolsEnabled).
export function showDevtoolsPanel(pixiAppInstance) {
  if (!pixiAppInstance || !isDevtoolsEnabledFromUrl()) return () => {};

  const panelElement = createElement('div', {
    boxSizing: 'border-box',
    zIndex: '1000',
    position: 'absolute',
    top: '0',
    right: '0',
    width: '100%',
    maxWidth: 'min(340px, 90vw)',
    maxHeight: '100dvh',
    padding: '8px',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'black',
    color: 'white',
    fontFamily: 'Arial, sans-serif',
    fontSize: '12px',
    fontWeight: 'bold',
    overflow: 'visible',
    transition: 'transform 160ms ease',
    willChange: 'transform',
  });

  const hidePanelButton = createButton('→', {
    position: 'absolute',
    top: '0',
    left: '-28px',
    width: '28px',
    minWidth: '28px',
    height: '58px',
    padding: '0',
    opacity: '0.72',
    border: '0',
    borderRadius: '0',
    backgroundColor: 'rgba(34, 34, 34, 0.75)',
  });
  hidePanelButton.title = 'Hide devtools panel';

  const showPanelButton = createButton('←', {
    position: 'absolute',
    top: '0',
    left: '50%',
    width: '58px',
    minWidth: '58px',
    height: '28px',
    padding: '0',
    opacity: '0.72',
    border: '0',
    borderRadius: '0',
    backgroundColor: 'rgba(34, 34, 34, 0.75)',
    display: 'none',
    transform: 'translateX(-50%)',
  });
  showPanelButton.title = 'Show devtools panel';

  hidePanelButton.addEventListener('click', () => {
    panelElement.style.transform = 'translateX(100%)';
    hidePanelButton.style.display = 'none';
    showPanelButton.style.display = 'block';
  });

  showPanelButton.addEventListener('click', () => {
    panelElement.style.transform = 'translateX(0)';
    showPanelButton.style.display = 'none';
    hidePanelButton.style.display = 'block';
  });
  panelElement.appendChild(hidePanelButton);

  const headerElement = createElement('div', {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: '0',
    gap: '8px',
  });

  const metrics = createMetricsSection(pixiAppInstance);
  const drawerToggleButton = createButton('Show', {
    flexShrink: '0',
  });

  headerElement.appendChild(metrics.element);
  headerElement.appendChild(drawerToggleButton);
  panelElement.appendChild(headerElement);

  const drawer = createQueryParamsDrawer();
  let isDrawerOpen = false;

  if (drawer) {
    panelElement.appendChild(drawer);

    drawerToggleButton.addEventListener('click', () => {
      isDrawerOpen = !isDrawerOpen;
      drawer.style.display = isDrawerOpen ? 'block' : 'none';
      drawerToggleButton.textContent = isDrawerOpen ? 'Hide' : 'Show';
    });
  } else {
    drawerToggleButton.disabled = true;
    drawerToggleButton.title = 'getQueryParams is not available';
  }

  document.body.appendChild(panelElement);
  document.body.appendChild(showPanelButton);

  return () => {
    metrics.destroy();
    showPanelButton.remove();
    panelElement.remove();
  };
}
